import React, { useEffect, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { WhatsAppSettingsConfig } from '../types';

type Props = {
  settings: WhatsAppSettingsConfig;
  onChange: (next: WhatsAppSettingsConfig) => void;
  onValidationChange?: (error: string | null) => void;
};

const toInt = (value: string, fallback: number): number => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(max, Math.max(min, value));
};

const WhatsAppSettingsForm: React.FC<Props> = ({ settings, onChange, onValidationChange }) => {
  const delayError = useMemo(() => {
    if (settings.dispatch_delay_max_seconds < settings.dispatch_delay_min_seconds) {
      return 'La demora máxima debe ser mayor o igual que la demora mínima.';
    }

    return null;
  }, [settings.dispatch_delay_max_seconds, settings.dispatch_delay_min_seconds]);

  useEffect(() => {
    onValidationChange?.(delayError);
  }, [delayError, onValidationChange]);

  const update = <K extends keyof WhatsAppSettingsConfig>(key: K, value: WhatsAppSettingsConfig[K]) => {
    onChange({
      ...settings,
      [key]: value,
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="wa-active-provider">Proveedor activo</Label>
          <div className="h-9 flex items-center">
            <Badge variant="secondary" className="uppercase">{settings.active_provider}</Badge>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="wa-max-recipients">Máximo destinatarios por lote</Label>
          <Input
            id="wa-max-recipients"
            type="number"
            min={1}
            max={2000}
            value={settings.max_recipients_per_dispatch}
            onChange={(event) => update('max_recipients_per_dispatch', clamp(toInt(event.target.value, 1), 1, 2000))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="wa-idempotency-window">Ventana idempotencia (minutos)</Label>
          <Input
            id="wa-idempotency-window"
            type="number"
            min={1}
            max={1440}
            value={settings.idempotency_window_minutes}
            onChange={(event) => update('idempotency_window_minutes', clamp(toInt(event.target.value, 1), 1, 1440))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="wa-temp-link-expires">Expiración enlace temporal (horas)</Label>
          <Input
            id="wa-temp-link-expires"
            type="number"
            min={0}
            max={168}
            value={settings.temp_link_expires_hours}
            onChange={(event) => update('temp_link_expires_hours', clamp(toInt(event.target.value, 0), 0, 168))}
          />
          <p className="text-xs text-muted-foreground">0 = sin expiración</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="wa-delay-min">Demora mínima entre envíos (seg)</Label>
          <Input
            id="wa-delay-min"
            type="number"
            min={0}
            max={300}
            value={settings.dispatch_delay_min_seconds}
            onChange={(event) => update('dispatch_delay_min_seconds', clamp(toInt(event.target.value, 0), 0, 300))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="wa-delay-max">Demora máxima entre envíos (seg)</Label>
          <Input
            id="wa-delay-max"
            type="number"
            min={0}
            max={300}
            aria-invalid={!!delayError}
            value={settings.dispatch_delay_max_seconds}
            onChange={(event) => update('dispatch_delay_max_seconds', clamp(toInt(event.target.value, 0), 0, 300))}
          />
        </div>
      </div>

      {delayError && (
        <p className="text-sm text-red-600">{delayError}</p>
      )}

      <div className="space-y-3 rounded-lg border p-4">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="wa-duplicate-check" className="cursor-pointer">
            Activar control de duplicados por idempotencia
          </Label>
          <Checkbox
            id="wa-duplicate-check"
            checked={settings.duplicate_check_enabled}
            onCheckedChange={(checked) => update('duplicate_check_enabled', checked === true)}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="wa-temp-link-unlimited" className="cursor-pointer">
            Enlace temporal sin límite de descargas
          </Label>
          <Checkbox
            id="wa-temp-link-unlimited"
            checked={settings.temp_link_max_downloads === null}
            onCheckedChange={(checked) => {
              const unlimited = checked === true;
              update('temp_link_max_downloads', unlimited ? null : 1);
            }}
          />
        </div>

        {settings.temp_link_max_downloads !== null && (
          <div className="space-y-2">
            <Label htmlFor="wa-temp-link-max-downloads">Máximo descargas por enlace</Label>
            <Input
              id="wa-temp-link-max-downloads"
              type="number"
              min={1}
              max={1000}
              value={settings.temp_link_max_downloads}
              onChange={(event) => update('temp_link_max_downloads', clamp(toInt(event.target.value, 1), 1, 1000))}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppSettingsForm;
